import { isNil } from 'lodash-es';

import { ComponentsCheckFilters } from './types';
import {
  ComponentWeightBuffer,
  CookingStationName,
  CookingStations,
  FoodComponent,
  FoodComponentMethodStep,
  FoodComponentStationTitle,
  MeasurementUnit
} from '../../libs';
import { ComponentService } from '../../libs/utils/componentService';

export const formatWeightText = (weight: number, weightPerMeasurmentUnit: number, measurementUnit: MeasurementUnit) =>
  measurementUnit === MeasurementUnit.g
    ? weight + ' ' + measurementUnit
    : `${weight} g` + ` (${weight / (weightPerMeasurmentUnit || 1) + ' ' + measurementUnit})`;

export const calculateFractionWeight = (fc: FoodComponent, quantity: number, cookedWeight: number, ingWeight?: number) => {
  const totalWeight = ComponentService.calculateTotalComponentAbsoluteWeight(fc);
  const fraction = (quantity * (ingWeight || 1)) / totalWeight;
  const ingQuantity = cookedWeight * (fc.cookedRawFactor ?? 1) * fraction;
  return ingQuantity;
};

export const addStepsToMethods = (methods: FoodComponentMethodStep[]): (FoodComponentMethodStep & { step: number })[] => {
  let currentStation: string | undefined = 'initialStation';
  let currentStep = 0;

  return methods.map((method) => {
    if (
      (!method.station && !(isNil(currentStation) || currentStation === '')) ||
      (method.station && method.station !== currentStation)
    ) {
      currentStation = method.station;
      currentStep = 0;
    } else {
      currentStep++;
    }

    return { ...method, step: currentStep };
  });
};

export const orderIngredients = (foodComponent: FoodComponent) => {
  return foodComponent.ingredients ?? [];
};

export const getLabel = (foodComponent: FoodComponent) => {
  const componentName = typeof foodComponent.name === 'string' ? foodComponent.name : foodComponent.name?.en || 'Unknown';

  return componentName;
};

export const calculateBufferPercentage = (cookedWeight: number, buffers: ComponentWeightBuffer[]): number => {
  if (!buffers || buffers.length === 0) {
    return 0;
  }

  const sortedBuffers = [...buffers].sort((a, b) => {
    const endRangeA = a.endRange === Number.MAX_SAFE_INTEGER ? Infinity : (a.endRange ?? Infinity);
    const endRangeB = b.endRange === Number.MAX_SAFE_INTEGER ? Infinity : (b.endRange ?? Infinity);
    const rangeA = endRangeA - a.startRange;
    const rangeB = endRangeB - b.startRange;
    return rangeA - rangeB;
  });

  for (const buffer of sortedBuffers) {
    const { startRange, endRange, percentage } = buffer;

    if (endRange === null || endRange === undefined || endRange === Number.MAX_SAFE_INTEGER) {
      if (cookedWeight >= startRange) {
        return percentage;
      }
    } else {
      if (cookedWeight >= startRange && cookedWeight <= endRange) {
        return percentage;
      }
    }
  }

  return 0;
};

export const getFirstUnfinishedStation = (foodComponent: FoodComponent): CookingStations | null => {
  const stations = foodComponent.cookingStations ?? [];
  const verified = foodComponent.isVerified;
  const lastStation = stations[stations.length - 1];

  if (verified) {
    return lastStation;
  }

  for (const station of stations) {
    if (!station.endTime) {
      return station;
    }
  }
  return lastStation;
};

export const getDefaultFilters = (): ComponentsCheckFilters => ({
  stations: [Object.keys(FoodComponentStationTitle)[0]] as unknown as CookingStationName[],
  statuses: []
});

export const COMPONENT_CHECK_STATIONS_STORAGE_KEY = 'componentsCheck.stationsFilter';

export const getPersistedStationsFilter = (): CookingStationName[] => {
  try {
    const persistedValue = localStorage.getItem(COMPONENT_CHECK_STATIONS_STORAGE_KEY);
    if (!persistedValue) return [];

    const parsedValue: unknown = JSON.parse(persistedValue);
    if (!Array.isArray(parsedValue)) return [];

    const validStations = new Set(Object.keys(FoodComponentStationTitle));
    return parsedValue.filter(
      (station): station is CookingStationName => typeof station === 'string' && validStations.has(station)
    );
  } catch {
    return [];
  }
};
